import React from 'react';
import { useTranslation } from 'react-i18next';

import { LineChart, Skeleton, DayBars, type ChartSeries, type DayBarSeries } from '../../../../components';
import { HOME_SERIES_COLORS } from '../../../../theme/tokens';
import type { HomeData, HomePeriod } from '../../../../mock-data/types';
import { MetricFrame } from '../MetricFrame';
import { PrimaryStat } from '../PrimaryStat';
import { Stat } from '../Stat';
import { Legend } from '../Legend';
import { Inner, Grid, LeftCol, Divider, SplitRow, ChartCol, LegendWrap } from './FinancialPerformance.styles';

// Compact money axis formatter ("$700", "$1.2k", "$-341.3k") — abbreviates by
// magnitude so large NEGATIVE values (e.g. a loss-making GGR) compact too.
const moneyFmt = (n: number): string => {
  const abs = Math.abs(n);
  return abs >= 1000 ? `$${n < 0 ? '-' : ''}${Math.round(abs / 100) / 10}k` : `$${Math.round(n)}`;
};

// Mirrors the LineChart `height` so the placeholder reserves the same band.
const CHART_HEIGHT = 150;

export type FinancialPerformanceProps = {
  data: HomeData['financial'];
  /** Delta caption ("vs yesterday" / "vs last month" / …) — adapts to the range. */
  comparison: string;
  /** The selected range — drives whether the trend chart or the day-comparison bars
   *  show (Today/Yesterday have no usable hourly slices). */
  interval: HomePeriod;
  /** Opens Financial Performance in Stats: the whole card becomes a button. */
  onOpen?: () => void;
  /** While true, the figures + chart show placeholders (the endpoint is loading). */
  loading?: boolean;
};

/**
 * "Financial Performance" — the Gross Revenue lead figure with Stakes / Wins
 * split stats, beside a three-series GGR / Stakes / Wins chart (Wins dashed) with
 * a money-formatted Y axis. On the Today / Yesterday ranges the chart collapses to
 * a prior-day vs current-day {@link DayBars} comparison.
 */
export const FinancialPerformance = ({
  data,
  comparison,
  interval,
  loading = false,
  onOpen,
}: FinancialPerformanceProps): React.ReactElement => {
  const { t } = useTranslation();
  const dayView = interval === 'TODAY' || interval === 'YESTERDAY';
  const series: ChartSeries[] = [
    { name: t('GGR'), color: HOME_SERIES_COLORS.ggr, points: data.chart.ggr },
    { name: t('Stakes'), color: HOME_SERIES_COLORS.stakes, points: data.chart.stakes },
    { name: t('Wins'), color: HOME_SERIES_COLORS.wins, points: data.chart.wins, dashed: true },
  ];
  const dayBars: DayBarSeries[] = [
    { name: t('GGR'), color: HOME_SERIES_COLORS.ggr, ...data.days.ggr },
    { name: t('Stakes'), color: HOME_SERIES_COLORS.stakes, ...data.days.stakes },
    { name: t('Wins'), color: HOME_SERIES_COLORS.wins, dashed: true, ...data.days.wins },
  ];

  // A tap on the day bars reads a bar's value (as the line chart's taps do): it must not also open the full view.
  const touchTap = React.useRef(false);

  return (
    <MetricFrame title={t('Financial Performance')} sub={t('Conversion from Handle to Gross Profit')} onOpen={onOpen}>
      <Inner>
        <Grid>
          <LeftCol>
            <PrimaryStat
              label={t('Gross Revenue')}
              value={data.grossRevenue.value}
              prefix={data.grossRevenue.prefix}
              delta={data.grossRevenue.delta}
              sub={comparison}
              loading={loading}
              error={data.grossRevenue.error}
            />
            <Divider />
            <SplitRow>
              <Stat label={t('Stakes')} value={data.stakes.value} prefix={data.stakes.prefix} suffix={data.stakes.suffix} delay={100} compact loading={loading} error={data.stakes.error} />
              <Stat label={t('Wins')} value={data.wins.value} prefix={data.wins.prefix} suffix={data.wins.suffix} delay={200} compact loading={loading} error={data.wins.error} />
            </SplitRow>
          </LeftCol>
          <ChartCol>
            {loading ? (
              <Skeleton block height={CHART_HEIGHT} radius={12} />
            ) : dayView ? (
              <div
                onPointerDown={(event: React.PointerEvent<HTMLDivElement>) => {
                  touchTap.current = event.pointerType !== 'mouse';
                }}
                onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                  if (touchTap.current) event.stopPropagation();
                }}
              >
                <DayBars series={dayBars} view={interval === 'TODAY' ? 'today' : 'yesterday'} height={CHART_HEIGHT} formatValue={moneyFmt} />
              </div>
            ) : (
              <LineChart series={series} xLabels={data.xLabels} pointLabels={data.pointLabels} height={CHART_HEIGHT} yTicks={3} formatY={moneyFmt} animate />
            )}
            <LegendWrap>
              <Legend
                items={[
                  { name: t('GGR'), color: HOME_SERIES_COLORS.ggr },
                  { name: t('Stakes'), color: HOME_SERIES_COLORS.stakes },
                  { name: t('Wins'), color: HOME_SERIES_COLORS.wins, dashed: true },
                ]}
              />
            </LegendWrap>
          </ChartCol>
        </Grid>
      </Inner>
    </MetricFrame>
  );
};

export default FinancialPerformance;
